/**
 * N6 — Tutoriel-éclair affiché au tour 0 d'un nouveau mandat.
 *
 * Explique en 4 cartes les axes politiques que les décisions vont
 * faire dériver. Skippable, persisté localStorage : ne réapparaît pas
 * au refresh ni au mandat suivant — sauf reset complet.
 */

import { useEffect, useState } from "react";

const STORAGE_KEY = "mp.tutorialSeen.v1";

const AXES = [
  {
    label: "ÉCONOMIQUE",
    poles: "libéral ←→ interventionniste",
    desc: "Marché libre vs régulation par l'État. Visible dans vos arbitrages fiscaux et sociaux.",
  },
  {
    label: "SOCIÉTAL",
    poles: "conservateur ←→ progressiste",
    desc: "Tradition vs émancipation. Les ruptures symboliques pèsent fortement sur cet axe.",
  },
  {
    label: "INTERNATIONAL",
    poles: "souverainiste ←→ ouvert",
    desc: "Repli national vs intégration. Vos choix diplomatiques modifient les relations avec 7 pays.",
  },
  {
    label: "AUTORITÉ",
    poles: "autoritaire ←→ libertaire",
    desc: "Ordre public vs libertés individuelles. La répression et la sécurité montent ici.",
  },
];

interface Props {
  /** Tour courant — l'overlay ne s'affiche qu'à T0 (avant la 1ère décision). */
  turn: number;
}

export function TutorialOverlay({ turn }: Props) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (turn !== 0) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "yes") return;
    } catch {
      /* ignore */
    }
    setShown(true);
  }, [turn]);

  const close = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "yes");
    } catch {
      /* ignore */
    }
    setShown(false);
  };

  if (!shown) return null;

  return (
    <div className="tutorial-backdrop" onClick={close}>
      <div
        className="tutorial-card"
        role="dialog"
        aria-modal="true"
        aria-label="Comment fonctionne Moi Président"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tutorial-pretitle">PRISE EN MAIN · 30 SECONDES</div>
        <h2 className="tutorial-title">Vos décisions dérivent sur 4 axes.</h2>
        <p className="tutorial-intro">
          À chaque option choisie, votre profil politique bouge. Au tour 8, il
          se cristallise en archétype (« Néoconservateur », « Sociale-démocrate
          », etc.). À T60, le bilan dira qui vous êtes.
        </p>

        <ol className="tutorial-axes">
          {AXES.map((a) => (
            <li key={a.label} className="tutorial-axis">
              <div className="tutorial-axis-label">{a.label}</div>
              <div className="tutorial-axis-poles">{a.poles}</div>
              <div className="tutorial-axis-desc">{a.desc}</div>
            </li>
          ))}
        </ol>

        <div className="tutorial-tip">
          Survolez un indicateur pour son explication. Cliquez pour ses détails.
        </div>

        <button type="button" className="tutorial-close" onClick={close}>
          ━━ COMMENCER ━━
        </button>
      </div>
    </div>
  );
}
